import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'

import ActionsDropdown from '../../../../features/components/ActionsDropdown'
import ItemsLoading from '../../../../components/Loaders/ItemsLoading'
import List from '../../../../features/components/List'
import SkeletonListRow from '../../../../features/components/List/Skeleton'
import PageSection from '../../../../features/components/PageSection'

import { createJsonFromQueryString } from '../../../../helpers/url'

import { openForm } from '../../../../modules/actions/forms'
import { deleteLocationList, getLocationLists } from '../../../../modules/actions/location'
import {
  deleteLocationListIdSelector,
  locationListsErrorSelector,
  locationListsLoadingSelector,
  locationListsNextSelector,
  locationListsSelector,
  locationListsWasLoadedSelector,
  updateLocationListIdSelector
} from '../../../../modules/selectors/location'

import { listItemsTableColumnsMinWidth } from '../../../../features/components/ListItemsTable/columnsWidth'
import { LOCATION_LIST_EDIT } from '../../../../constants/forms'

import useCommonStyles from '../../../../styles/common/listPage'

const LocationList = () => {
  const commonClasses = useCommonStyles()

  const dispatch = useDispatch()
  const { t } = useTranslation()

  const locationLists = useSelector(locationListsSelector)
  const locationListsNext = useSelector(locationListsNextSelector)
  const updateLocationListId = useSelector(updateLocationListIdSelector)
  const deleteLocationListId = useSelector(deleteLocationListIdSelector)

  const loadMoreHandler = useCallback(() => {
    if (locationListsNext) {
      // put query params to action
      dispatch(getLocationLists(createJsonFromQueryString(`?${locationListsNext.split('?')[1]}`)))
    }
  }, [dispatch, locationListsNext])

  const openEditFormHandler = useCallback(
    id => {
      dispatch(openForm({ id: id, formName: LOCATION_LIST_EDIT }))
    },
    [dispatch]
  )

  const deleteLocationListHandler = useCallback(
    id => {
      dispatch(deleteLocationList(id))
    },
    [dispatch]
  )

  const getLocationListDropdownOptions = useCallback(
    id => {
      return [
        {
          text: t('Edit'),
          onClickHandler: () => openEditFormHandler(id)
        },
        {
          text: t('Delete'),
          onClickHandler: () => deleteLocationListHandler(id)
        }
      ]
    },
    [deleteLocationListHandler, openEditFormHandler, t]
  )

  const locationListColumns = useMemo(
    () => [
      {
        headTitle: 'Name',
        field: 'name',
        style: {
          minWidth: 250
        }
      },
      {
        headTitle: 'Type',
        field: 'location_list_type'
      },
      {
        headTitle: 'Number of locations',
        field: 'size',
        style: {
          display: 'flex',
          justifyContent: 'flex-end'
        }
      },
      {
        Component: ({ id }) => <ActionsDropdown closeOnInsideClick options={getLocationListDropdownOptions(id)} />,
        style: {
          minWidth: listItemsTableColumnsMinWidth.dropdown,
          maxWidth: listItemsTableColumnsMinWidth.dropdown,
          padding: 0
        }
      }
    ],
    [getLocationListDropdownOptions]
  )

  const skeletonProps = useMemo(() => ({ cols: locationListColumns }), [locationListColumns])

  return (
    <ItemsLoading
      className={commonClasses.itemList}
      itemsLength={locationLists.length}
      isLoadingSelector={locationListsLoadingSelector}
      wasLoadedSelector={locationListsWasLoadedSelector}
      errorSelector={locationListsErrorSelector}
      loadMore={locationListsNext && loadMoreHandler}
      SkeletonComponent={SkeletonListRow}
      skeletonProps={skeletonProps}
      ContentWrapper={PageSection}
      isEmptyNoDataMessage
    >
      <List
        cols={locationListColumns}
        listData={locationLists}
        itemUpdatingId={updateLocationListId}
        itemDeletingId={deleteLocationListId}
        Skeleton={<SkeletonListRow cols={locationListColumns} key={uuidv4()} />}
      />
    </ItemsLoading>
  )
}

export default LocationList
